/**
 * Settings stores of apps that the shell doesn't use at startup. Each one loads with its app (preloaded when navigation to the
 * app starts, otherwise on the app's first useGlobalSetting call) instead of being in the startup bundle.
 *
 * To add an app's settings store: add a lazy slot for its key in GlobalSettingStore.ts and an entry here, with the app
 * registry ids (keys in Apps/AppRegistry.ts) whose pages use it. tsc fails if the two lists don't match.
 */
export const appSettingLoaders = {
    corpIdentityApp: {
        apps: ["CorpIdentity"],
        load: async () => (await import(/* webpackChunkName: "settings-corpIdentityApp" */ "Apps/CorpIdentity/Store/CorpIdentitySettings")).CorpIdentitySettings
    },
    umsApp: {
        apps: ["UMS"],
        load: async () => (await import(/* webpackChunkName: "settings-umsApp" */ "Apps/UMS/Store/UMSSettings")).UMSSettings
    },
    customerHubApp: {
        apps: ["CustomerHub"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-customerHubApp" */ "Apps/CustomerHub/Stores/CustomerHubAppSetting")).CustomerHubAppSetting
    },
    teamsApp: {
        apps: ["Teams"],
        load: async () => (await import(/* webpackChunkName: "settings-teamsApp" */ "Apps/Teams/UserTeamsSettings")).UserTeamsSettings
    },
    ic3SpogApp: {
        apps: ["IC3SPoG", "IC3IEG"],
        load: async () => (await import(/* webpackChunkName: "settings-ic3SpogApp" */ "Apps/IC3SPoG/Stores/IC3SPoGSettings")).IC3SPoGSettings
    },
    ic3IEGApp: {
        apps: ["IC3IEG"],
        load: async () => (await import(/* webpackChunkName: "settings-ic3IEGApp" */ "Apps/IC3IEG/Store/IC3IEGSettings")).IC3IEGSettings
    },
    contactsApp: {
        apps: ["Contacts"],
        load: async () => (await import(/* webpackChunkName: "settings-contactsApp" */ "Apps/Contacts/UserContactsSettings")).UserContactsSettings
    },
    runtimeInsightsApp: {
        apps: ["RuntimeInsights"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-runtimeInsightsApp" */ "Apps/RuntimeInsights/RuntimeInsightsAppSettings")).RuntimeInsightsAppSettings
    },
    rulesApp: {
        apps: ["Rules"],
        load: async () => (await import(/* webpackChunkName: "settings-rulesApp" */ "Apps/Rules/UserRulesSettings")).UserRulesSettings
    },
    mpaApp: {
        apps: ["MPA"],
        load: async () => (await import(/* webpackChunkName: "settings-mpaApp" */ "Apps/MPA/UserMPASetting")).UserMPASettings
    },
    signalHealthApp: {
        apps: ["M365Health"],
        load: async () => (await import(/* webpackChunkName: "settings-signalHealthApp" */ "Apps/Health/Stores/SignalHealthSettings")).SignalHealthSettings
    },
    deploymentHubApp: {
        apps: ["DeploymentHub"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-deploymentHubApp" */ "Apps/DeploymentHub/Stores/DeploymentHubSetting")).DeploymentHubSetting
    },
    sovCloudOpsApp: {
        apps: ["SovCloudOps"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-sovCloudOps" */ "Apps/SovereignCloudOps/Shared/Store/SovCloudOpsStore")).SovCloudOpsSettings
    },
    statusFranceApp: {
        apps: ["SovCloudOps"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-sovCloudOps" */ "Apps/SovereignCloudOps/Shared/Store/SovCloudOpsStore")).StatusFranceSettings
    },
    recoveryControlPanelApp: {
        apps: ["RecoveryControlPanel"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-recoveryControlPanelApp" */ "Apps/RecoveryControlPanel/Stores/RecoveryControlPanelSettings"))
                .RecoveryControlPanelSettings
    },
    TorusWebDMSSettings: {
        apps: ["Torus"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-TorusWebDMSSettings" */ "Apps/Torus/Components/WebDMS/TorusWebDMSSettings")).TorusWebDMSSettings
    },
    transportQueueApp: {
        apps: ["TransportQueue"],
        load: async () =>
            (await import(/* webpackChunkName: "settings-transportQueueApp" */ "Apps/TransportQueue/Store/TransportQueueSettings")).TransportQueueSettings
    },
    r9UserSettings: {
        apps: ["R9"],
        load: async () => (await import(/* webpackChunkName: "settings-r9UserSettings" */ "Apps/R9/Stores/R9UserSetting")).R9UserSettings
    },
    sarcApp: {
        apps: ["Sarc"],
        load: async () => (await import(/* webpackChunkName: "settings-sarcApp" */ "Apps/Sarc/Stores/SarcAppSettings")).SarcAppSettings
    },
    agencyHubApp: {
        apps: ["AgencyHub"],
        load: async () => (await import(/* webpackChunkName: "settings-agencyHubApp" */ "Apps/AgencyHub/AgencyHubAppSetting")).AgencyHubAppSetting
    }
};

export type LazyAppSettingKey = keyof typeof appSettingLoaders;
export type LazyAppSettingType<K extends LazyAppSettingKey> = Awaited<ReturnType<(typeof appSettingLoaders)[K]["load"]>>;

export const lazyAppSettingKeys = Object.keys(appSettingLoaders).filter((key): key is LazyAppSettingKey => key in appSettingLoaders);
